/**
 * 通知システム
 * 
 * @file NotificationSystem.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * アプリケーション全体で使用するトースト通知システム。
 * 成功、エラー、警告、情報の4種類の通知を自動消去機能付きで提供。
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Close,
  CheckCircle,
  Error,
  Warning,
  Info,
  CloudUpload,
  CloudDownload,
  Sync,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  IconButton,
  Box,
  Typography,
  Slide,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

// 通知の種類
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

// 通知アクション
export interface NotificationAction {
  label: string;
  onClick: () => void;
  color?: 'primary' | 'secondary' | 'inherit';
}

// 通知オブジェクト
export interface Notification {
  id: string;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number | null; // null = 手動で閉じるまで表示
  actions?: NotificationAction[];
  persistent?: boolean;
  icon?: React.ReactNode;
  timestamp?: Date;
  progress?: number; // 0-100 (プログレスバー用)
  link?: string; // クリック時の遷移先（アプリ内相対パス）
}

// 通知設定
export interface NotificationOptions {
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  maxNotifications?: number;
  defaultDuration?: number;
  showProgress?: boolean;
  groupSimilar?: boolean;
}

// デフォルト設定（defaultDuration は NotificationProvider の options 必須注入で供給される）
const defaultOptions: Omit<Required<NotificationOptions>, 'defaultDuration'> = {
  position: { vertical: 'bottom', horizontal: 'right' },
  maxNotifications: 5,
  showProgress: false,
  groupSimilar: true,
};

// アイコンマッピング
const iconMap: Record<NotificationType, React.ReactNode> = {
  success: <CheckCircle />,
  error: <Error />,
  warning: <Warning />,
  info: <Info />,
};

// 通知コンテキスト
export interface NotificationContextValue {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  updateNotification: (id: string, updates: Partial<Notification>) => void;
  // ショートカットメソッド
  success: (message: string, options?: Partial<Notification>) => string;
  error: (message: string, options?: Partial<Notification>) => string;
  warning: (message: string, options?: Partial<Notification>) => string;
  info: (message: string, options?: Partial<Notification>) => string;
  // 特殊な通知タイプ
  uploadProgress: (fileName: string, progress: number) => string;
  downloadProgress: (fileName: string, progress: number) => string;
  syncProgress: (message: string, progress: number) => string;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

// 通知プロバイダー
export const NotificationProvider: React.FC<{
  children: React.ReactNode;
  options: NotificationOptions & { defaultDuration: number };
}> = ({ children, options }) => {
  if (options.defaultDuration === undefined || options.defaultDuration === null) {
    throw new globalThis.Error('[NotificationSystem] options.defaultDuration は必須です。DB設定値（ms単位）を注入してください。');
  }
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const config = useMemo(() => ({ ...defaultOptions, ...options }), [options]);

  const generateId = useCallback(() => {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const addNotification = useCallback((
    notification: Omit<Notification, 'id' | 'timestamp'>,
  ): string => {
    const id = generateId();
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date(),
      duration: notification.duration !== undefined ? notification.duration : config.defaultDuration,
    };

    setNotifications(prev => {
      // 類似通知のグループ化
      if (config.groupSimilar) {
        const existingIndex = prev.findIndex(
          n => n.type === newNotification.type && n.message === newNotification.message,
        );
        if (existingIndex>= 0) {
          // 既存の通知を更新
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], timestamp: new Date() };
          return updated;
        }
      }

      // 最大数を超える場合は古い通知を削除
      const updated = [...prev, newNotification];
      if (updated.length> config.maxNotifications) {
        return updated.slice(-config.maxNotifications);
      }
      return updated;
    });

    // 自動削除タイマー
    if (newNotification.duration && newNotification.duration> 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  }, [config, generateId, removeNotification]);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const updateNotification = useCallback((id: string, updates: Partial<Notification>) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, ...updates } : n),
    );
  }, []);

  // ショートカットメソッド
  const success = useCallback((message: string, options?: Partial<Notification>) => {
    return addNotification({ type: 'success', message, ...options });
  }, [addNotification]);

  const error = useCallback((message: string, options?: Partial<Notification>) => {
    return addNotification({
      type: 'error',
      message,
      duration: null, // エラーは手動で閉じるまで表示
      ...options,
    });
  }, [addNotification]);

  const warning = useCallback((message: string, options?: Partial<Notification>) => {
    return addNotification({ type: 'warning', message, ...options });
  }, [addNotification]);

  const info = useCallback((message: string, options?: Partial<Notification>) => {
    return addNotification({ type: 'info', message, ...options });
  }, [addNotification]);

  // プログレス通知
  const uploadProgress = useCallback((fileName: string, progress: number) => {
    const id = `upload-${fileName}`;
    const existing = notifications.find(n => n.id === id);
    
    if (existing) {
      updateNotification(id, { progress, message: `${fileName} をアップロード中... ${progress}%` });
      return id;
    } else {
      const notification = {
        type: 'info' as const,
        title: 'アップロード中',
        message: `${fileName} をアップロード中... ${progress}%`,
        icon: <CloudUpload />,
        progress,
        duration: null,
      };
      return addNotification(notification);
    }
  }, [addNotification, updateNotification, notifications]);

  const downloadProgress = useCallback((fileName: string, progress: number) => {
    const id = `download-${fileName}`;
    const existing = notifications.find(n => n.id === id);
    
    if (existing) {
      updateNotification(id, { progress, message: `${fileName} をダウンロード中... ${progress}%` });
      return id;
    } else {
      const notification = {
        type: 'info' as const,
        title: 'ダウンロード中',
        message: `${fileName} をダウンロード中... ${progress}%`,
        icon: <CloudDownload />,
        progress,
        duration: null,
      };
      return addNotification(notification);
    }
  }, [addNotification, updateNotification, notifications]);

  const syncProgress = useCallback((message: string, progress: number) => {
    const id = 'sync-progress';
    const existing = notifications.find(n => n.id === id);
    
    if (existing) {
      updateNotification(id, { progress, message: `${message} ${progress}%` });
      return id;
    } else {
      const notification = {
        type: 'info' as const,
        title: '同期中',
        message: `${message} ${progress}%`,
        icon: <Sync />,
        progress,
        duration: null,
      };
      return addNotification(notification);
    }
  }, [addNotification, updateNotification, notifications]);

  const value: NotificationContextValue = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    updateNotification,
    success,
    error,
    warning,
    info,
    uploadProgress,
    downloadProgress,
    syncProgress,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer options={config} />
    </NotificationContext.Provider>
  );
};

// 通知コンテナー
const NotificationContainer: React.FC<{
  options: Required<NotificationOptions>;
}> = ({ options }) => {
  const { notifications, removeNotification } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerSx: SxProps<Theme> = {
    position: 'fixed',
    zIndex: theme.zIndex.snackbar,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    p: 2,
    maxWidth: isMobile ? '100%' : 400,
    width: isMobile ? '100%' : 'auto',
    ...(options.position.vertical === 'top' ? { top: 0 } : { bottom: 0 }),
    ...(options.position.horizontal === 'left' && { left: 0 }),
    ...(options.position.horizontal === 'center' && {
      left: '50%',
      transform: 'translateX(-50%)',
    }),
    ...(options.position.horizontal === 'right' && { right: 0 }),
  };

  return (
    <Box sx={containerSx}>
      {notifications.map((notification, index) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onClose={() => removeNotification(notification.id)}
          index={index}
          position={options.position}
          showProgress={options.showProgress}
        />
      ))}
    </Box>
  );
};

// 通知アイテム
const NotificationItem: React.FC<{
  notification: Notification;
  onClose: () => void;
  index: number;
  position: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' };
  showProgress: boolean;
}> = ({ notification, onClose, index, position, showProgress }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const slideDirection = position.horizontal === 'left' ? 'right' : 'left';
  const handleNotificationClick = () => {
    if (notification.link) {
      navigate(notification.link);
    }
  };

  return (
    <Slide
      direction={slideDirection}
      in={true}
      timeout={{
        enter: theme.transitions.duration.enteringScreen,
        exit: theme.transitions.duration.leavingScreen,
      }}
      style={{
        transitionDelay: `${index * 100}ms`,
      }}>
      <Alert
        severity={notification.type}
        icon={notification.icon || iconMap[notification.type]}
        onClick={notification.link ? handleNotificationClick : undefined}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {notification.actions?.map((action) => (
              <IconButton
                key={`${notification.id}-action-${action.label}`}
                color={action.color || 'inherit'}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                }}
                aria-label={action.label}>
                {action.label}
              </IconButton>
            ))}
            <IconButton
              color="inherit"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="通知を閉じる">
              <Close fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          width: '100%',
          boxShadow: theme.shadows[6],
          cursor: notification.link ? 'pointer' : 'default',
          '& .MuiAlert-message': {
            flex: 1,
          },
        }}>
        {notification.title && (
          <AlertTitle>{notification.title}</AlertTitle>
        )}
        <Typography variant="body2">{notification.message}</Typography>
        
        {notification.timestamp && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {notification.timestamp.toLocaleTimeString()}
          </Typography>
        )}

        {/* プログレスバー */}
        {showProgress && notification.progress !== undefined && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                width: '100%',
                height: 4,
                backgroundColor: 'var(--alpha-white-03)',
                borderRadius: 2,
                overflow: 'hidden',
              }}>
              <Box
                sx={{
                  width: `${notification.progress}%`,
                  height: '100%',
                  backgroundColor: 'currentColor',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>
        )}
      </Alert>
    </Slide>
  );
};

// 通知フック
export const useNotifications = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new globalThis.Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// プリセット通知
export const NotificationPresets = {
  // システム通知
  systemUpdate: (version: string) => ({
    type: 'info' as const,
    title: 'システム更新',
    message: `バージョン ${version} が利用可能です`,
    actions: [
      { label: '更新', onClick: () => {} },
      { label: '後で', onClick: () => {} },
    ],
  }),

  // ネットワーク関連
  offline: () => ({
    type: 'warning' as const,
    title: 'オフライン',
    message: 'インターネット接続が切断されました',
    persistent: true,
  }),

  online: () => ({
    type: 'success' as const,
    message: 'インターネット接続が復旧しました',
    duration: 3000,
  }),

  // 操作完了
  saveSuccess: () => ({
    type: 'success' as const,
    message: '保存しました',
    duration: 3000,
  }),

  deleteSuccess: (itemName: string) => ({
    type: 'success' as const,
    message: `${itemName} を削除しました`,
    actions: [
      { label: '元に戻す', onClick: () => {} },
    ],
    duration: 8000,
  }),

  // エラー
  serverError: () => ({
    type: 'error' as const,
    title: 'サーバーエラー',
    message: 'サーバーに接続できません。しばらく後にお試しください。',
    actions: [
      { label: '再試行', onClick: () => {} },
    ],
    duration: null,
  }),

  validationError: (errors: string[]) => ({
    type: 'error' as const,
    title: '入力エラー',
    message: errors.join(', '),
    duration: 8000,
  }),
};

// コンポーネントエクスポート
export default {
  NotificationProvider,
  useNotifications,
  NotificationPresets,
};
